import { useState, useCallback, useEffect, useRef, SetStateAction, Dispatch } from 'react';
import { useSupabaseClient, useSession } from '@supabase/auth-helpers-react';
import { InvoiceDetailData, InvoiceLineItem, resolveClaimTitle } from '../../config';

/**
 * All editable invoice fields owned by the editor. One state object means
 * one auto-save dep, one save-payload mapping site, and one fetch-seeding
 * site. Adding a new editable column requires adding the key here, in the
 * seed below, and in the editor's save payload — three predictable spots.
 */
export interface EditableFields {
  lineItems: InvoiceLineItem[];
  claimTitle: string;
  serviceDescription: string;
  poNumber: string;
  invoiceDate: string;
  recordedDate: string;
  contactOverride: string;
  projectDescriptionOverride: string;
  siteAddressOverride: string;
  careOfOverride: string;
}

const EMPTY_FIELDS: EditableFields = {
  lineItems: [],
  claimTitle: '',
  serviceDescription: '',
  poNumber: '',
  invoiceDate: '',
  recordedDate: '',
  contactOverride: '',
  projectDescriptionOverride: '',
  siteAddressOverride: '',
  careOfOverride: '',
};

function seedFieldsFromInvoice(detail: InvoiceDetailData): EditableFields {
  let serviceDescription = detail.invoice.service_description ?? '';
  if (!serviceDescription) {
    serviceDescription = detail.prior_service_description ?? '';
  }
  if (!serviceDescription) {
    if (detail.project?.types === 'RWC') {
      serviceDescription = 'RETAINING WALL INSPECTIONS & CERTIFICATION';
    } else if (detail.project?.types === 'RWD') {
      serviceDescription = 'Retaining wall certificate of design compliance';
    } else if (detail.project?.types === 'Design') {
      serviceDescription = 'Civil and Structural Engineering Services';
    } else if (detail.project?.types === 'Advisory') {
      serviceDescription = 'Structural Engineering Services';
    }
  }

  let poNumber = detail.invoice.po_number ?? '';
  if (!poNumber) {
    poNumber = detail.stage?.po ?? detail.project?.po ?? '';
  }

  return {
    lineItems: detail.line_items || [],
    claimTitle: resolveClaimTitle(detail.invoice.claim_title, detail.invoice_position),
    serviceDescription,
    poNumber,
    invoiceDate: detail.invoice.invoice_date ?? '',
    recordedDate: detail.invoice.recorded_date ?? '',
    contactOverride: detail.invoice.contact_override ?? '',
    projectDescriptionOverride: detail.invoice.project_description_override ?? '',
    siteAddressOverride: detail.invoice.site_address_override ?? '',
    careOfOverride: detail.invoice.care_of_override ?? '',
  };
}

export interface UseInvoiceLoadResult {
  invoiceData: InvoiceDetailData | null;
  fields: EditableFields;
  setLineItems: Dispatch<SetStateAction<InvoiceLineItem[]>>;
  setClaimTitle: Dispatch<SetStateAction<string>>;
  setServiceDescription: Dispatch<SetStateAction<string>>;
  setPoNumber: Dispatch<SetStateAction<string>>;
  setInvoiceDate: Dispatch<SetStateAction<string>>;
  setRecordedDate: Dispatch<SetStateAction<string>>;
  setContactOverride: Dispatch<SetStateAction<string>>;
  setProjectDescriptionOverride: Dispatch<SetStateAction<string>>;
  setSiteAddressOverride: Dispatch<SetStateAction<string>>;
  setCareOfOverride: Dispatch<SetStateAction<string>>;
  refetch: () => Promise<void>;
  /**
   * The exact `fields` object last written by a fetch. Compare by identity to
   * tell "the server seeded this" from "the user edited this" - re-seeding
   * must not mark the invoice dirty or trigger an auto-save.
   */
  lastSeededFields: EditableFields;
  refreshProjectData: () => Promise<InvoiceDetailData | null>;
  refreshPoFromProject: () => Promise<void>;
  loading: boolean;
  error: string | null;
  /** True after the first fetch settles. Use to guard auto-save / seeding effects. */
  hasLoaded: boolean;
}

function makeFieldSetter<K extends keyof EditableFields>(
  key: K,
  setFieldsState: Dispatch<SetStateAction<EditableFields>>,
): Dispatch<SetStateAction<EditableFields[K]>> {
  return (value) => {
    setFieldsState(prev => {
      const next = typeof value === 'function'
        ? (value as (prev: EditableFields[K]) => EditableFields[K])(prev[key])
        : value;
      return { ...prev, [key]: next };
    });
  };
}

/**
 * Loads an invoice and owns the editable-fields state. Refresh helpers update
 * the joined data (project/stage/client/contact) without disturbing in-flight
 * edits.
 */
export function useInvoiceLoad(invoiceId: string | undefined): UseInvoiceLoadResult {
  const supabase = useSupabaseClient();
  const session = useSession();

  const [invoiceData, setInvoiceData] = useState<InvoiceDetailData | null>(null);
  const [fields, setFieldsState] = useState<EditableFields>(EMPTY_FIELDS);
  const [lastSeededFields, setLastSeededFields] = useState<EditableFields>(EMPTY_FIELDS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  // Stable per-field setters with SetStateAction semantics. ESLint can't see
  // through makeFieldSetter to confirm it only closes over the stable
  // setFieldsState, so the rule is suppressed per line.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setLineItems = useCallback(makeFieldSetter('lineItems', setFieldsState), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setClaimTitle = useCallback(makeFieldSetter('claimTitle', setFieldsState), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setServiceDescription = useCallback(makeFieldSetter('serviceDescription', setFieldsState), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setPoNumber = useCallback(makeFieldSetter('poNumber', setFieldsState), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setInvoiceDate = useCallback(makeFieldSetter('invoiceDate', setFieldsState), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setRecordedDate = useCallback(makeFieldSetter('recordedDate', setFieldsState), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setContactOverride = useCallback(makeFieldSetter('contactOverride', setFieldsState), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setProjectDescriptionOverride = useCallback(makeFieldSetter('projectDescriptionOverride', setFieldsState), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setSiteAddressOverride = useCallback(makeFieldSetter('siteAddressOverride', setFieldsState), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setCareOfOverride = useCallback(makeFieldSetter('careOfOverride', setFieldsState), []);

  const refetch = useCallback(async () => {
    if (!invoiceId) return;
    try {
      setLoading(true);
      setError(null);
      const { data, error: rpcError } = await supabase.rpc('get_invoice_detail', {
        p_invoice_id: parseInt(invoiceId),
      });
      if (rpcError) throw rpcError;
      if (!data) throw new Error('Invoice not found');
      const detail = data as InvoiceDetailData;
      const seeded = seedFieldsFromInvoice(detail);
      setInvoiceData(detail);
      setFieldsState(seeded);
      setLastSeededFields(seeded);
      hasLoadedRef.current = true;
    } catch (err) {
      console.error('Error fetching invoice:', err);
      setError(err instanceof Error ? err.message : 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }, [invoiceId, supabase]);

  const refreshProjectData = useCallback(async (): Promise<InvoiceDetailData | null> => {
    if (!invoiceId) return null;
    try {
      const { data, error: rpcError } = await supabase.rpc('get_invoice_detail', {
        p_invoice_id: parseInt(invoiceId),
      });
      if (rpcError || !data) return null;
      const detail = data as InvoiceDetailData;
      setInvoiceData(prev =>
        prev
          ? { ...prev, project: detail.project, stage: detail.stage, client: detail.client, contact: detail.contact }
          : detail
      );
      return detail;
    } catch (err) {
      console.error('Error refreshing project data:', err);
      return null;
    }
  }, [invoiceId, supabase]);

  const refreshPoFromProject = useCallback(async () => {
    const detail = await refreshProjectData();
    if (!detail) return;
    const po = detail.stage?.po ?? detail.project?.po ?? '';
    setPoNumber(po);
  }, [refreshProjectData, setPoNumber]);

  // Fetch once per invoice and signed-in user. Keyed on the user id, not the
  // session object: the auth helper emits a new session object on every auth
  // event (a tab regaining focus is enough), and re-fetching then re-seeds the
  // fields, discarding whatever the user had typed but not yet saved.
  const sessionUserId = session?.user?.id ?? null;
  useEffect(() => {
    if (!sessionUserId) return;
    void refetch();
  }, [sessionUserId, refetch]);

  return {
    invoiceData,
    fields,
    setLineItems,
    setClaimTitle,
    setServiceDescription,
    setPoNumber,
    setInvoiceDate,
    setRecordedDate,
    setContactOverride,
    setProjectDescriptionOverride,
    setSiteAddressOverride,
    setCareOfOverride,
    refetch,
    lastSeededFields,
    refreshProjectData,
    refreshPoFromProject,
    loading,
    error,
    hasLoaded: hasLoadedRef.current,
  };
}
